import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Document, Page, pdfjs } from 'react-pdf';
import SignaturePad from 'signature_pad';
import { PenSquare, CheckCircle, Loader2, X, Edit3 } from 'lucide-react';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

const API = '/api';

export default function SignPage() {
  const { token } = useParams();
  const [signingData, setSigningData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [completed, setCompleted] = useState(false);
  const [numPages, setNumPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [showSignPad, setShowSignPad] = useState(false);
  const [signatureData, setSignatureData] = useState('');
  const [fieldValues, setFieldValues] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [activeField, setActiveField] = useState(null);
  const padRef = useRef(null);
  const padInstance = useRef(null);

  useEffect(() => {
    axios.get(`${API}/sign/${token}`)
      .then(r => setSigningData(r.data))
      .catch(err => setError(err.response?.data?.detail || 'Invalid or expired signing link'))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    if (showSignPad && padRef.current) {
      padInstance.current = new SignaturePad(padRef.current, {
        backgroundColor: 'rgba(255,255,255,0)',
        penColor: '#1a1a1a',
      });
    }
    return () => {
      if (padInstance.current) {
        padInstance.current.off();
      }
    };
  }, [showSignPad]);

  const captureSignature = () => {
    if (!padInstance.current || padInstance.current.isEmpty()) {
      alert('Please draw your signature first');
      return;
    }
    const dataUrl = padInstance.current.toDataURL('image/png');
    setSignatureData(dataUrl);
    setShowSignPad(false);

    if (activeField) {
      setFieldValues(v => ({ ...v, [activeField.field_id]: dataUrl }));
      setActiveField(null);
    }
  };

  const handleFieldClick = (field) => {
    if (field.type === 'signature' || field.type === 'initials') {
      setActiveField(field);
      setShowSignPad(true);
    } else if (field.type === 'date') {
      const today = new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
      setFieldValues(v => ({ ...v, [field.field_id]: today }));
    } else if (field.type === 'text') {
      const val = prompt(`Enter ${field.label || 'text'}:`);
      if (val !== null) setFieldValues(v => ({ ...v, [field.field_id]: val }));
    } else if (field.type === 'checkbox') {
      setFieldValues(v => ({ ...v, [field.field_id]: v[field.field_id] === 'checked' ? '' : 'checked' }));
    }
  };

  const submit = async () => {
    const requiredFields = signingData?.fields.filter(f => f.required) || [];
    const missing = requiredFields.filter(f => {
      if (f.type === 'signature' || f.type === 'initials') {
        return !fieldValues[f.field_id] && !signatureData;
      }
      return f.type !== 'date' && !fieldValues[f.field_id];
    });

    if (missing.length > 0) {
      alert(`Please complete all required fields: ${missing.map(f => f.type).join(', ')}`);
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/sign/${token}/submit`, {
        signature_data_url: signatureData || null,
        field_values: fieldValues,
      });
      setCompleted(true);
    } catch (err) {
      alert(err.response?.data?.detail || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <Loader2 className="w-10 h-10 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-gray-50 text-center px-4">
        <X className="w-16 h-16 text-red-400 mb-4" />
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Link Not Valid</h1>
        <p className="text-gray-500 max-w-sm">{error}</p>
      </div>
    );
  }

  if (completed) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-gray-50 text-center px-4">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-12 max-w-md">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Document Signed!</h1>
          <p className="text-gray-500 mb-6">
            Thank you, <strong>{signingData?.signer_name}</strong>. All parties will receive the completed document by email.
          </p>
          <div className="flex items-center justify-center gap-2 text-blue-600 font-bold">
            <PenSquare className="w-5 h-5" /> EZSig
          </div>
        </div>
      </div>
    );
  }

  const currentPageFields = (signingData?.fields || []).filter(f => f.page === currentPage);

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between sticky top-0 z-40">
        <div className="flex items-center gap-2 font-bold text-blue-600">
          <PenSquare className="w-5 h-5" /> EZSig
        </div>
        <div className="text-sm text-gray-500 hidden sm:block">
          Signing as <strong>{signingData?.signer_name}</strong>
        </div>
        <button
          onClick={submit}
          disabled={submitting}
          className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-60"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
          Finish Signing
        </button>
      </div>

      <div className="max-w-4xl mx-auto py-6 px-4">
        <div className="bg-white rounded-xl p-4 mb-4 text-sm flex items-center gap-2">
          <span className="text-gray-600">
            <strong>{signingData?.document_name}</strong> — Click the highlighted fields to complete them.
          </span>
        </div>

        {/* PDF viewer with field overlays */}
        <div className="relative bg-white shadow-lg mx-auto overflow-hidden" style={{ width: 'fit-content' }}>
          <Document
            file={`${API}/sign/${token}/pdf`}
            onLoadSuccess={({ numPages }) => setNumPages(numPages)}
            loading={
              <div className="w-full min-h-[400px] flex items-center justify-center text-gray-400">
                <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading PDF...
              </div>
            }
          >
            <Page
              pageNumber={currentPage}
              width={Math.min(700, typeof window !== 'undefined' ? window.innerWidth - 48 : 700)}
            />
          </Document>

          {/* Field overlays */}
          {currentPageFields.map(field => {
            const value = fieldValues[field.field_id];
            const isSigField = field.type === 'signature' || field.type === 'initials';
            const isFilledSig = isSigField && (!!value || !!signatureData);
            const isFilled = isSigField ? isFilledSig : !!value;

            return (
              <div
                key={field.field_id}
                onClick={() => handleFieldClick(field)}
                style={{
                  position: 'absolute',
                  left: field.x,
                  top: field.y,
                  width: field.w,
                  height: field.h,
                  border: `2px solid ${isFilled ? '#10b981' : '#3b82f6'}`,
                  backgroundColor: isFilled ? 'rgba(16,185,129,0.1)' : 'rgba(59,130,246,0.15)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxSizing: 'border-box',
                  zIndex: 10,
                  overflow: 'hidden',
                  transition: 'background-color 0.2s',
                }}
              >
                {isSigField && (value || signatureData) ? (
                  <img
                    src={value || signatureData}
                    alt="signature"
                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                  />
                ) : field.type === 'checkbox' ? (
                  <div style={{ fontSize: 20, lineHeight: 1 }}>
                    {value === 'checked' ? '☑' : '☐'}
                  </div>
                ) : value ? (
                  <span style={{ fontSize: 11, padding: '2px 4px', color: '#1a1a1a' }}>{value}</span>
                ) : (
                  <div className="flex items-center gap-1" style={{ color: '#3b82f6', fontSize: 10 }}>
                    <Edit3 style={{ width: 10, height: 10 }} />
                    <span className="capitalize font-medium">{field.type}</span>
                    {field.required && <span style={{ color: '#ef4444' }}>*</span>}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Page nav */}
        {numPages > 1 && (
          <div className="flex items-center justify-center gap-4 mt-4">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm disabled:opacity-40 hover:bg-gray-50"
            >
              ← Prev
            </button>
            <span className="text-sm text-gray-600">Page {currentPage} of {numPages}</span>
            <button
              onClick={() => setCurrentPage(p => Math.min(numPages, p + 1))}
              disabled={currentPage === numPages}
              className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm disabled:opacity-40 hover:bg-gray-50"
            >
              Next →
            </button>
          </div>
        )}

        <div className="mt-6 text-center">
          <button
            onClick={submit}
            disabled={submitting}
            className="inline-flex items-center gap-2 bg-green-600 text-white px-8 py-3 rounded-xl text-lg font-medium hover:bg-green-700 disabled:opacity-60"
          >
            {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle className="w-5 h-5" />}
            Complete & Submit Signature
          </button>
        </div>
      </div>

      {/* Signature pad modal */}
      {showSignPad && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-lg shadow-2xl">
            <h2 className="text-xl font-bold mb-1">Draw Your Signature</h2>
            <p className="text-sm text-gray-500 mb-4">Use your mouse or touch to sign in the box below</p>
            <div
              className="border-2 border-gray-300 rounded-lg mb-4 bg-white"
              style={{ touchAction: 'none' }}
            >
              <canvas
                ref={padRef}
                width={500}
                height={200}
                className="w-full"
                style={{ touchAction: 'none', display: 'block' }}
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => padInstance.current?.clear()}
                className="flex-1 border border-gray-300 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-50"
              >
                Clear
              </button>
              <button
                onClick={() => { setShowSignPad(false); setActiveField(null); }}
                className="flex-1 border border-gray-300 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={captureSignature}
                className="flex-1 bg-blue-600 text-white py-2.5 rounded-lg text-sm font-medium hover:bg-blue-700"
              >
                Apply Signature
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
